import * as React from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
const render = (status: Status) => {
  return <></>;
};

type TGoogleMap = {
  onClick: (e: any) => void;
  click?: google.maps.LatLng;
  newCenter?: google.maps.LatLngLiteral;
  newZoom?: number;
  notEditable?: boolean;
};

export const GoogleMap = ({
  newCenter,
  newZoom = 15,
  click,
  onClick: onClickMap,
  notEditable = false,
}: TGoogleMap) => {
  const [clicks, setClicks] = React.useState<google.maps.LatLng>();
  const [zoom, setZoom] = React.useState(newZoom); // initial zoom
  const [center, setCenter] = React.useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  });

  const onClick = (e: google.maps.MapMouseEvent) => {
    return;
    if (notEditable) return;
    setClicks(e.latLng!);
    onClickMap(e.latLng!);
  };

  React.useEffect(() => {
    newZoom && setZoom(newZoom);
    newCenter && setCenter(newCenter);
    /*     click && setClicks(click); */
  }, [newCenter, newZoom, click]);

  const onIdle = (m: google.maps.Map) => {
    onClickMap && onClickMap(m.getCenter()!.toJSON());
    /*  setZoom(m.getZoom()!);
    setCenter(m.getCenter()!.toJSON()); */
  };

  /* const form = (
    <div
      style={{
        padding: "1rem",
        flexBasis: "250px",
        height: "100%",
        overflow: "auto",
      }}>
      <label htmlFor="zoom">Zoom</label>
      <input type="number" id="zoom" name="zoom" value={zoom} onChange={event => setZoom(Number(event.target.value))} />
      <br />
      <label htmlFor="lat">Latitude</label>
      <input
        type="number"
        id="lat"
        name="lat"
        value={center.lat}
        onChange={event => setCenter({ ...center, lat: Number(event.target.value) })}
      />
      <br />
      <label htmlFor="lng">Longitude</label>
      <input
        type="number"
        id="lng"
        name="lng"
        value={center.lng}
        onChange={event => setCenter({ ...center, lng: Number(event.target.value) })}
      />
      <h3>{clicks.length === 0 ? "Click on map to add markers" : "Clicks"}</h3>
      {clicks.map((latLng, i) => (
        <pre key={i}>{JSON.stringify(latLng.toJSON(), null, 2)}</pre>
      ))}
      <button onClick={() => setClicks([])}>Clear</button>
    </div>
  ); */

  return (
    <div style={{ display: 'flex', height: '100%', width: '100%' }}>
      <Wrapper apiKey={process.env.REACT_APP_FIREBASE_API_KEY!} render={render}>
        <Map
          draggable={!notEditable}
          center={center}
          onClick={onClick}
          onIdle={onIdle}
          zoom={zoom}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          clickableIcons={false}
          style={{ flexGrow: '1', height: '100%' }}
          mapTypeId={'roadmap'}
          styles={[
            {
              featureType: 'poi',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'transit',
              stylers: [
                {
                  visibility: 'off',
                },
              ],
            },
            {
              featureType: 'poi.park',
              elementType: 'all',
              stylers: [
                {
                  visibility: 'on',
                },
              ],
            },
          ]}
        >
          {clicks && <Marker key={'direction-market'} position={clicks} />}
        </Map>
      </Wrapper>
      {/* Basic form for controlling center and zoom of map. */}
      {/*      {form} */}
    </div>
  );
};

interface MapProps extends google.maps.MapOptions {
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
}

const Map: React.FC<MapProps> = ({
  onClick,
  onIdle,
  children,
  style,
  ...options
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new (window as any).google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  React.useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName),
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map } as any);
        }
      })}
    </>
  );
};

const Marker: React.FC<google.maps.MarkerOptions> = (options) => {
  const [marker, setMarker] = React.useState<google.maps.Marker>();

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker]);

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options);
    }
  }, [marker, options]);

  return null;
};

const deepCompareEqualsForMaps = (a: any, b: any) => {
  if (
    isLatLngLiteral(a) ||
    a instanceof google.maps.LatLng ||
    isLatLngLiteral(b) ||
    b instanceof google.maps.LatLng
  ) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
  }
};

function useDeepCompareMemoize(value: any) {
  const ref = React.useRef();
  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: any[],
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
